import React, { useEffect, useState } from 'react';
import ISelectOption from '@/models/generic/iselect-option';
import * as tripsService from '@/services/trips-service';
import * as configService from '@/services/config-service';
import DropdownComponent from '@/components/forms/dropdown-component';
import NumberComponent from '@/components/forms/number-component';
import TextAreaComponent from '@/components/forms/textarea-component';
import DatePickerComponent from "@/components/forms/datepicker-component";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import YesAndNoDropdownComponent from "@/components/forms/yes-no-dropdown-component";
import ITrip from "@/models/trips/itrip";
import { TripHasChanged, TripHasFormData, TripHasRequiredFormFields } from "./validation-trip";

interface IProps {
    id?: number;
    isInstructor: boolean;
    modelValidation: (hasRequiredFormFields: boolean, hasFormData: boolean, hasChanged: boolean, model: ITrip) => void;
    isLoading: (isLoading: boolean) => void;
}

const EditTripComponent: React.FC<IProps> = ({ id, isInstructor, modelValidation, isLoading }) => {

    const [ trip, setTrip] = useState<ITrip>({} as ITrip);
    const [ bookingOptions, setBookingOptions ] = useState<ISelectOption[]>([]);
    const [ roomTypes, setRoomTypes ] = useState<ISelectOption[]>([]);
    const [ originalTrip, setOrginalTrip] = useState<ITrip>({} as ITrip);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // eslint-disable-next-line prefer-const
                let [ trip, room_types, booking_options ] = await Promise.all([
                    !!id && tripsService.getTrip(id),
                    configService.GetRoomTypes(),
                    configService.GetBookingOptions()
                ]) ;

                setBookingOptions(booking_options);
                setRoomTypes(room_types);
                
                // Set defaults on new Trip
                if (!trip) {
                    trip = {
                        reservationDate: new Date(),
                        isCancelled: false
                    } as ITrip;
                }
                
                setTrip(trip);
                setOrginalTrip(trip);
                
                modelValidation(
                    TripHasRequiredFormFields(trip),
                    TripHasFormData(trip),
                    TripHasChanged(originalTrip, trip),
                    trip);
            } catch {
                toast.error("The trip information couldn't be loaded for an unknown reason.");
            }

            isLoading(false);
        }

        fetchData();
    }, [])

    useEffect(() => {
        modelValidation(
            TripHasRequiredFormFields(trip),
            TripHasFormData(trip),
            TripHasChanged(originalTrip, trip),
            trip
        );
    }, [trip])

    if (isInstructor) {
        return (
            <>
                <DropdownComponent
                    value={trip?.bookedRoomTypeId}
                    values={roomTypes}
                    purpose="Booked Room Type *"
                    newValue={(newValue) => setTrip({...trip, bookedRoomTypeId: Number(newValue)} as ITrip)} 
                />
            </>
        );
    } else {
        return (
            <>
                <DatePickerComponent
                    value={trip?.reservationDate}
                    purpose="Reservation Date *"
                    newValue={(newValue) => setTrip({...trip, reservationDate: newValue} as ITrip)} 
                />

                <DropdownComponent
                    value={trip?.bookedRoomTypeId}
                    values={roomTypes}
                    purpose="Booked Room Type *"
                    newValue={(newValue) => setTrip({...trip, bookedRoomTypeId: Number(newValue)} as ITrip)} 
                />

                <NumberComponent
                    value={trip?.fullPrice}
                    purpose="Full Price"
                    newValue={(newValue) => setTrip({...trip, fullPrice: Number(newValue)} as ITrip)}
                />

                <NumberComponent
                    value={trip?.discount}
                    purpose="Discount (%)"
                    newValue={(newValue) => setTrip({...trip, discount: Number(newValue)} as ITrip)} 
                />            

                <TextAreaComponent
                    value={trip?.staffNotes}
                    purpose="Notes about this order"
                    newValue={(newValue) => setTrip({...trip, staffNotes: newValue} as ITrip)} 
                />

                <TextAreaComponent
                    value={trip?.guestNotes}
                    purpose="Notes from the guest"
                    newValue={(newValue) => setTrip({...trip, guestNotes: newValue} as ITrip)} 
                />

                <DropdownComponent
                    value={trip?.bookedViaId}
                    values={bookingOptions}
                    purpose="Booked Via"
                    newValue={(newValue) => setTrip({...trip, bookedViaId: Number(newValue)} as ITrip)} 
                /> 

                <YesAndNoDropdownComponent
                    value={trip?.isCancelled}
                    alternativeYes="Cancelled"
                    alternativeNo="Active"
                    purpose="Booking status"
                    newValue={(newValue) => setTrip({...trip, isCancelled: newValue} as ITrip)}
                />
            </>
        );
    }
}

export default EditTripComponent;